import {
  FsAvgInvestmentChartData, FsAvgInvestmentMappedData,
  FsChartsData,
  FsChartsOptions,
  FsExitSizeChartData, FsExitSizeMappedData, FsPieMappedData,
  FsProceedsByGroupChartsData, FsProceedsBySizeChartsData,
} from "../pojo/fs-charts-classes";
import {
  addNotTransparentTooltipBackground, CHART_COLORS,
  CHART_FONTS, CHART_STYLES,
  formatNumberWithSuffix,
  HighchartsOptions
} from "../../../../shared/component/highcharts-common/highcharts-global-const";


export class FsChartOptionsService {

  static prepareOptions(data: FsChartsData): FsChartsOptions {
    const options = new FsChartsOptions();
    if (!data) {
      return options;
    }
    options.barExitSize = this.createBarExitSize(data.barExitSize);
    options.barAvgInv = this.createBarAvgInv(data.barAvgInv);
    options.barProceedsByGroup = this.createBarProceedsByGroup(data.barProceedsByGroup);
    options.pieProceedsBySize = this.createPieProceedsBySize(data.pieProceedsBySize);
    return options;
  }

  private static createBarExitSize(data: FsExitSizeChartData[]): Partial<HighchartsOptions> {
    const sortedData = [...data].sort((a, b) => b.exitCompValue - a.exitCompValue);
    const mappedData = new FsExitSizeMappedData();

    mappedData.category = sortedData.map((item) =>
      (item.compNumber === 1 ? `${item.compNumber} company` : `${item.compNumber} companies`),
    );
    mappedData.exitCompValue = sortedData.map((item) => item.exitCompValue);
    mappedData.avgInitialHolding = sortedData.map((item) => (item.avgInitialHolding ? item.avgInitialHolding * 100 : null));
    mappedData.avgExitHolding = sortedData.map((item) => (item.avgExitHolding ? item.avgExitHolding * 100 : null));

    return {
      tooltip: {
        shared: true,
        split: false,
        borderColor: '#EEEFF0',
        borderRadius: 12,
        borderWidth: 0, // Disable SVG border -> fix bug tooltip layover the legend
        backgroundColor: 'transparent', // Disable SVG background -> fix bug tooltip layover the legend
        shadow: false,
        useHTML: true,
        style: { fontSize: CHART_FONTS.font4 },
        formatter(): string {
          let tooltip = '';

          this.points.forEach((point): void => {
            const colorDot = '<span style="color: ' + point.color + '">&#9679;</span>';
            let formattedValue;

            if (point.series.name.includes('holding')) {
              formattedValue = point.y.toFixed(1) + '%';
            } else {
              formattedValue = formatNumberWithSuffix(point.y, 1);
            }

            tooltip += colorDot + ' ' + point.series.name + ': <b>' + formattedValue + '</b><br>';
          });

          return addNotTransparentTooltipBackground(tooltip);
        },
      },
      legend: CHART_STYLES.legend('center'),
      xAxis: {
        categories: mappedData.category,
        type: 'category',
        labels: {
          style: {
            fontSize: CHART_FONTS.font4,
            color: CHART_COLORS.black12,
            textOverflow: 'ellipsis',
            width: 100,
          },
        },
        scrollbar: CHART_STYLES.scrollbar,
      },
      yAxis: [
        {
          title: undefined,
          labels: CHART_STYLES.yAxisLabelsStyleWithSuffix,
        },
        {
          title: undefined,
          labels: CHART_STYLES.yAxisLabelsStyleWithPcnt,
          opposite: true,
        },
      ],
      plotOptions: {
        column: {
          dataLabels: {
            enabled: true,
            style: {
              fontSize: CHART_FONTS.font4,
              color: CHART_COLORS.black12,
            },
            formatter(): string {
              return this.y === 0 ? '' : formatNumberWithSuffix(this.y, 1);
            },
          },
        },
      },
      series: [
        {
          name: 'Exit company value',
          data: mappedData.exitCompValue,
          color: CHART_COLORS.main8,
          type: 'column',
          yAxis: 0,
        },
        {
          name: 'Avg. initial holding',
          data: mappedData.avgInitialHolding,
          color: CHART_COLORS.vibrantMagenta,
          type: 'line',
          yAxis: 1,
          lineWidth: 0,
          marker: {
            enabled: true,
            radius: 3,
            symbol: 'circle',
            lineWidth: 2,
            lineColor: CHART_COLORS.vibrantMagenta,
            fillColor: CHART_COLORS.white,
          },
          states: {
            hover: {
              lineWidthPlus: 0,
            },
          },
        },
        {
          name: 'Avg. holding at exit',
          data: mappedData.avgExitHolding,
          color: CHART_COLORS.orange,
          type: 'line',
          yAxis: 1,
          lineWidth: 0,
          marker: {
            enabled: true,
            radius: 3,
            symbol: 'circle',
            lineWidth: 2,
            lineColor: CHART_COLORS.orange,
            fillColor: CHART_COLORS.white,
          },
          states: {
            hover: {
              lineWidthPlus: 0,
            },
          },
        },
      ],
    };
  }

  private static createBarAvgInv(data: FsAvgInvestmentChartData[]): Partial<HighchartsOptions> {
    const mappedData = new FsAvgInvestmentMappedData();
    mappedData.category = data.map((item) => {
      if (item.isWriteOff) {
        return `${item.compNumber === 1 ? `${item.compNumber} company` : `${item.compNumber} companies`}<br> Write-off`;
      }
      const exitValueWithSuffix = formatNumberWithSuffix(item.exitCompValue, 1);
      const companyText = item.compNumber === 1 ? 'company' : 'companies';
      return `${item.compNumber} ${companyText}<br>${exitValueWithSuffix}`;
    });
    mappedData.proceeds = data.map((item) => (item.avgProceeds ? item.avgProceeds : null));
    mappedData.investment = data.map((item) => (item.avgInv ? item.avgInv : null));
    mappedData.moic = data.map((item) => (item.avgMoic ? item.avgMoic : null));

    return {
      tooltip: {
        shared: true,
        borderColor: '#EEEFF0',
        borderWidth: 0, // Disable SVG border -> fix bug tooltip layover the legend
        backgroundColor: 'transparent', // Disable SVG background -> fix bug tooltip layover the legend        borderRadius: 12,
        shadow: false,
        useHTML: true,
        style: { fontSize: CHART_FONTS.font4 },
        formatter(): string {
          let tooltip = '';

          this.points.forEach((point): void => {
            const colorDot = '<span style="color: ' + point.color + '">&#9679;</span>';
            let formattedValue;

            if (point.series.name === 'MOIC') {
              formattedValue = 'x' + point.y.toFixed(2);
            } else {
              formattedValue = formatNumberWithSuffix(point.y, 1);
            }

            tooltip += colorDot + ' ' + point.series.name + ': <b>' + formattedValue + '</b><br>';
          });
          return addNotTransparentTooltipBackground(tooltip);
        },
      },
      legend: CHART_STYLES.legend('center'),
      xAxis: {
        categories: mappedData.category,
        type: 'category',
        labels: CHART_STYLES.xAxisLabels,
        scrollbar: CHART_STYLES.scrollbar,
      },
      yAxis: [
        {
          title: undefined,
          labels: CHART_STYLES.yAxisLabelsStyleWithSuffix,
        },
        {
          title: undefined,
          labels: CHART_STYLES.yAxisLabelsStyleWithX,
          opposite: true,
        },
      ],
      plotOptions: {
        column: {
          grouping: true,
          pointPadding: 0.1,
          groupPadding: 0.15,
        },
      },
      series: [
        {
          name: 'Proceeds',
          data: mappedData.proceeds,
          color: CHART_COLORS.main8,
          type: 'column',
          yAxis: 0,
          dataLabels: {
            enabled: true,
            style: {
              fontSize: CHART_FONTS.font4,
              color: CHART_COLORS.black12,
            },
            formatter(): string {
              return this.y === 0 ? '' : formatNumberWithSuffix(this.y, 1);
            },
          },
        },
        {
          name: 'Investment',
          data: mappedData.investment,
          color: CHART_COLORS.grey,
          type: 'column',
          yAxis: 0,
        },
        {
          name: 'MOIC',
          data: mappedData.moic,
          color: CHART_COLORS.orange,
          type: 'line',
          yAxis: 1,
          visible: true,
          lineWidth: 0,
          marker: {
            enabled: true,
            radius: 3,
            symbol: 'circle',
            lineWidth: 2,
            lineColor: CHART_COLORS.orange,
            fillColor: CHART_COLORS.white,
          },
          states: {
            hover: {
              lineWidthPlus: 0,
            },
          },
        },
      ],
    };
  }

  private static createBarProceedsByGroup(data: FsProceedsByGroupChartsData[]): Partial<HighchartsOptions> {
    const sortedData = [...data].sort((a, b) => (b.gain + b.investment) - (a.gain + a.investment));

    const categories = sortedData.map((item) => item.groupName);

    const gainData = sortedData.map((item) => item.gain || 0);
    const investmentData = sortedData.map((item) => item.investment || 0);
    const companyNumbers = sortedData.map((item) => item.compNumber);

    return {
      tooltip: {
        shared: true,
        borderColor: '#EEEFF0',
        borderWidth: 0,
        backgroundColor: 'transparent',
        borderRadius: 12,
        shadow: false,
        useHTML: true,
        style: { fontSize: CHART_FONTS.font4 },
        formatter(): string {
          let tooltip = '';

          // Get company number from the first point's index
          const pointIndex = this.points[0]?.point?.index;
          const compNumber = pointIndex !== undefined ? companyNumbers[pointIndex] : undefined;

          this.points.forEach((point): void => {
            const colorDot = '<span style="color: ' + point.color + '">&#9679;</span>';
            const formattedValue = formatNumberWithSuffix(point.y, 1);
            const seriesName = point.series.name;
            tooltip += colorDot + ' <b>' + formattedValue + '  </b>' + seriesName + '<br>';
          });

          if (compNumber) {
            const companyText = compNumber === 1 ? 'company' : 'companies';
            tooltip += '<span style="color: #6F777B; font-size: 0.8rem">' + compNumber + ' ' + companyText + '</span>';
          }

          return addNotTransparentTooltipBackground(tooltip);
        },
      },
      legend: CHART_STYLES.legend('center', false, 'bottom'),
      xAxis: {
        categories: categories,
        type: 'category',
        labels: {
          enabled: false,
        },
        scrollbar: CHART_STYLES.scrollbar,
      },
      yAxis: {
        title: undefined,
        labels: CHART_STYLES.yAxisLabelsStyleWithSuffix,
        stackLabels: {
          enabled: true,
          style: { color: CHART_COLORS.black12, fontSize: CHART_FONTS.font4 },
          formatter(): string {
            const value = this.total;
            let label = '';

            if (this.x !== undefined && categories[this.x]) {
              const categoryLabel = categories[this.x];
              const maxLines = 2;
              const maxCharsPerLine = 20;

              const displayLines: string[] = [];
              let remaining = categoryLabel;

              for (let i = 0; i < maxLines && remaining.length > 0; i++) {
                if (remaining.length > maxCharsPerLine) {
                  displayLines.push(remaining.substring(0, maxCharsPerLine));
                  remaining = remaining.substring(maxCharsPerLine);
                } else {
                  displayLines.push(remaining);
                  remaining = '';
                }
              }

              const isOverflow = remaining.length > 0;

              label = displayLines.join('<br>');
              if (isOverflow) {
                label += '...';
              }
              label += '<br>';
            }

            if (value !== 0) {
              label += formatNumberWithSuffix(value, 1);
            }

            return label;
          },
        },
      },
      plotOptions: {
        column: {
          stacking: 'normal',
          dataLabels: {
            enabled: false,
          },
        },
      },
      series: [
        {
          name: 'Gain',
          data: gainData,
          color: CHART_COLORS.main8,
          type: 'column',
          dataLabels: CHART_STYLES.dataLabelsWithSuffix,
        },
        {
          name: 'Investment',
          data: investmentData,
          color: CHART_COLORS.grey,
          type: 'column',
          dataLabels: CHART_STYLES.dataLabelsWithSuffix,
        },
      ],
    };
  }

  private static createPieProceedsBySize(data: FsProceedsBySizeChartsData[]): Partial<HighchartsOptions> {
    const mappedData = data.map((item) => this.mapPieDataForSize(item));
    return this.createBasePieChartOptions(mappedData, data, 'Proceeds');
  }

  private static mapPieDataForSize(item: FsProceedsBySizeChartsData): FsPieMappedData {
    const pieMappedData = new FsPieMappedData();
    const exitValueWithSuffix = formatNumberWithSuffix(item.exitCompValue, 1);
    const proceedsWithSuffix = formatNumberWithSuffix(item.sumProceeds, 1);
    const companyText = item.compNumber === 1 ? 'company' : 'companies';
    pieMappedData.name = `${exitValueWithSuffix}<br>${proceedsWithSuffix}<br>${item.compNumber} ${companyText}`;
    pieMappedData.y = item.sumProceeds;
    return pieMappedData;
  }

  /**
   * Create base pie chart options with common configuration
   */
  private static createBasePieChartOptions(
    mappedData: FsPieMappedData[],
    data: { sumProceeds: number }[],
    seriesName?: string,
  ): Partial<HighchartsOptions> {
    const totalProceeds = data.reduce((sum, item) => sum + item.sumProceeds, 0);
    const totalFormattedWithSuffix = formatNumberWithSuffix(totalProceeds, 1);

    return {
      tooltip: {
        enabled: false,
      },
      title: {
        text: totalFormattedWithSuffix,
        align: 'center',
        verticalAlign: 'middle',
        style: CHART_STYLES.titleStyle,
      },
      plotOptions: {
        pie: {
          innerSize: '60%',
          allowPointSelect: true,
          cursor: 'pointer',
          dataLabels: {
            enabled: true,
            useHTML: true,
            formatter(): string {
              const lines = this.point.name.split('<br>');
              const color = this.color;
              return `
                <div style="text-align: left;">
                  <div style="font-size: 0.75rem; padding-bottom: 0.5rem"><span style="color: ${color};">●</span> ${lines[0] || ''} exit</div>
                  <div style="font-size: 1rem; font-weight: bold; color: #263238;">${lines[1] || ''}</div>
                  <div style="font-size: 0.85rem; color: #6F777B;">${lines[2] || ''}</div>
                </div>
              `;
            },
          },
          size: '70%',
          states: CHART_STYLES.pieChartStates,
        },
      },
      series: [{
        ...(seriesName && { name: seriesName }),
        data: mappedData,
      }],
      colors: CHART_COLORS.default,
    };
  }
}
